import {
  InboxFilterState,
  InboxPaginationState,
  InboxSortDirection,
  InboxState,
} from 'features/inbox/types/types'
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

interface InboxContextValue {
  state: InboxState
  setSelectedInspection: (inspectionId?: string) => void
  setDrawerOpen: (open: boolean) => void
  setFilters: (filters: Partial<InboxFilterState>) => void
  setPagination: (pagination: Partial<InboxPaginationState>) => void
  setSortDirection: (sortDirection: InboxSortDirection) => void
}

const InboxContext = createContext<InboxContextValue | undefined>(undefined)

const defaultFilters: InboxFilterState = {
  statusFilters: [],
  inspectionTypeFilters: [],
  draLevelFilters: [],
  searchText: '',
  showFutureInspections: false,
}

const defaultPagination: InboxPaginationState = {
  currentPage: 1,
  pageSize: 20,
  total: 0,
}

const defaultState: InboxState = {
  selectedInspectionId: undefined,
  drawerOpen: false,
  filters: defaultFilters,
  pagination: defaultPagination,
  sortDirection: 'newest',
}

export const InboxProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<InboxState>(defaultState)

  const setSelectedInspection = useCallback((inspectionId?: string) => {
    setState((prev) => ({
      ...prev,
      selectedInspectionId: inspectionId,
      drawerOpen: !!inspectionId,
    }))
  }, [])

  const setDrawerOpen = useCallback((open: boolean) => {
    setState((prev) => ({
      ...prev,
      drawerOpen: open,
      selectedInspectionId: open ? prev.selectedInspectionId : undefined,
    }))
  }, [])

  const setFilters = useCallback((filters: Partial<InboxFilterState>) => {
    setState((prev) => ({
      ...prev,
      filters: { ...prev.filters, ...filters },
      pagination: { ...prev.pagination, currentPage: 1 },
    }))
  }, [])

  const setPagination = useCallback(
    (pagination: Partial<InboxPaginationState>) => {
      setState((prev) => ({
        ...prev,
        pagination: { ...prev.pagination, ...pagination },
      }))
    },
    []
  )

  const setSortDirection = useCallback((sortDirection: InboxSortDirection) => {
    setState((prev) => ({
      ...prev,
      sortDirection,
      pagination: { ...prev.pagination, currentPage: 1 },
    }))
  }, [])

  const value: InboxContextValue = useMemo(
    () => ({
      state,
      setSelectedInspection,
      setDrawerOpen,
      setFilters,
      setPagination,
      setSortDirection,
    }),
    [
      state,
      setSelectedInspection,
      setDrawerOpen,
      setFilters,
      setPagination,
      setSortDirection,
    ]
  )

  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>
}

export const useInbox = (): InboxContextValue => {
  const context = useContext(InboxContext)
  if (!context) {
    throw new Error('useInbox must be used within an InboxProvider')
  }
  return context
}
